/*
    Dragging and resizing the QR code on the back of a mail piece.

    WHY THIS IS NOT A LIBRARY. The constraint is not a rectangle. Lob stamps the address block,
    barcode and indicia onto the back after rendering our artwork, so the legal region is an L
    -- the column beside that block plus the strip above it -- and a code dropped on the block
    is not rejected, it is silently covered. Generic drag handles clamp to a box; this has to
    slide around one.

    WHAT MAKES IT SMOOTH, since that is the whole job:

      - Pointer events with `setPointerCapture`, so a fast drag that outruns the cursor keeps
        tracking instead of dropping the moment the pointer leaves the square.
      - The container rectangle is measured ONCE at pointer-down. Reading it per move forces
        layout on every frame, which is what makes a drag feel like it is catching.
      - Moves are coalesced into one `requestAnimationFrame`. A trackpad emits pointer events
        faster than the screen refreshes, so without this we do arithmetic and set state for
        frames nobody sees.
      - The live position lives in a ref and is painted by writing style directly, so a drag
        does not re-render the preview, the artwork iframe, or the card beside it. React state
        is set once, on release.
      - `touch-action: none` so a touch drag does not also scroll the page.

    Everything here is in FINISHED-card inches from the bottom-left, the coordinate system Lob
    measures `qr_code` in. See mailPieceTemplates for the conversion to canvas inches.
*/
import { useCallback, useEffect, useRef, useState } from "react";

import {
    clampQrPlacement, finishedSize, qrCanvasOrigin, qrPlacementError, QR_EDGE_MARGIN,
    type MailPieceSize, type QrPlacement,
} from "../mailPieceTemplates";

type Mode = "move" | "resize";

interface Props {
    size: MailPieceSize;
    placement: QrPlacement;
    /** Called once, on release. Dragging does not round-trip. */
    onChange: (next: QrPlacement) => void;
    /** Canvas inches, for converting pixels to inches against the rendered card. */
    canvas: { width: number; height: number };
    disabled?: boolean;
    /** The code glyph. Owned by the caller so this component is placement and nothing else. */
    children?: React.ReactNode;
}

export default function QrPlacer({ size, placement, onChange, canvas, disabled, children }: Props) {
    const boxRef = useRef<HTMLDivElement>(null);
    /*
        Why the code will not go where the pointer is.

        The clamp already makes an illegal placement impossible, so this changes no behaviour.
        It exists because a square that silently stops moving is a square somebody fights:
        dragging toward the address block and having it slide away looks like a bug until you
        are told the post office owns that corner. Set from the UNCLAMPED request, so it
        describes what was asked for rather than what was allowed.
    */
    const [limit, setLimit] = useState<string | null>(null);
    const drag = useRef<{
        mode: Mode;
        rect: DOMRect;
        startX: number;
        startY: number;
        from: QrPlacement;
        latest: QrPlacement;
        frame: number | null;
    } | null>(null);

    /** Paint without re-rendering. The source of truth stays the prop until release. */
    const paint = useCallback((p: QrPlacement) => {
        const el = boxRef.current;
        if (!el) return;
        const origin = qrCanvasOrigin(p);
        el.style.left = `${(origin.left / canvas.width) * 100}%`;
        el.style.bottom = `${(origin.bottom / canvas.height) * 100}%`;
        el.style.width = `${(origin.size / canvas.width) * 100}%`;
        el.style.height = `${(origin.size / canvas.height) * 100}%`;
    }, [canvas.width, canvas.height]);

    // Re-paint when the placement changes from outside a drag: a size change moves the card
    // under the code, and a clamped save can come back different from what was released.
    useEffect(() => { if (!drag.current) paint(placement); }, [placement, paint, size]);

    const onPointerDown = useCallback((mode: Mode) => (event: React.PointerEvent) => {
        if (disabled) return;
        event.preventDefault();
        event.stopPropagation();
        const card = boxRef.current?.parentElement;
        if (!card) return;

        (event.target as Element).setPointerCapture(event.pointerId);
        drag.current = {
            mode,
            // Measured once. Reading it per move is the difference between a drag that
            // tracks the cursor and one that lags behind it.
            rect: card.getBoundingClientRect(),
            startX: event.clientX,
            startY: event.clientY,
            from: placement,
            latest: placement,
            frame: null,
        };
    }, [disabled, placement]);

    const onPointerMove = useCallback((event: React.PointerEvent) => {
        const state = drag.current;
        if (!state) return;
        event.preventDefault();

        // Pixels to canvas inches, then to finished inches. The card is drawn at the canvas
        // aspect, so one axis cannot be assumed to scale like the other.
        const perInchX = state.rect.width / canvas.width;
        const perInchY = state.rect.height / canvas.height;
        const dx = (event.clientX - state.startX) / perInchX;
        // Screen y grows downward and the placement is measured from the bottom.
        const dy = -(event.clientY - state.startY) / perInchY;

        const next = state.mode === "move"
            ? { ...state.from, left: state.from.left + dx, bottom: state.from.bottom + dy }
            // Resize from the bottom-left anchor, so the corner under the pointer is the one
            // that moves and the code does not crawl away from the hand dragging it.
            // Whichever axis the hand actually moved along, keeping its sign. `Math.max(dx, dy)`
            // was wrong in a way no clamp test could catch: dragging the handle left gives a
            // negative dx against a zero dy, so the max was always 0 and the code could be
            // grown but never shrunk.
            //
            // Dominant axis rather than the diagonal projection `(dx + dy) / 2`, because that
            // halves the speed of a straight horizontal drag, which is how most people pull a
            // corner handle.
            : {
                ...state.from,
                width: state.from.width + (Math.abs(dx) >= Math.abs(dy) ? dx : dy),
            };

        state.latest = clampQrPlacement(size, next);

        // Cheap: two rectangle comparisons, and only when the answer changes. Computed off the
        // request rather than the result, because the result is legal by construction.
        const why = qrPlacementError(size, {
            left: round(next.left), bottom: round(next.bottom), width: round(next.width),
        });
        setLimit((current) => (current === why ? current : why));

        // One paint per frame. A trackpad emits moves faster than the screen refreshes.
        if (state.frame === null) {
            state.frame = requestAnimationFrame(() => {
                if (drag.current) {
                    paint(drag.current.latest);
                    drag.current.frame = null;
                }
            });
        }
    }, [canvas.width, canvas.height, size, paint]);

    const endDrag = useCallback(() => {
        const state = drag.current;
        if (!state) return;
        if (state.frame !== null) cancelAnimationFrame(state.frame);
        drag.current = null;
        setLimit(null);
        // Committed once, on release, so nothing upstream re-renders mid-drag.
        onChange(state.latest);
    }, [onChange]);

    useEffect(() => () => {
        if (drag.current?.frame !== null && drag.current?.frame !== undefined) {
            cancelAnimationFrame(drag.current.frame);
        }
    }, []);

    const origin = qrCanvasOrigin(placement);
    const card = finishedSize(size);

    /** Two decimals, matching what the clamp stores, so the message and the result agree. */
function round(value: number): number {
    return Math.round(value * 100) / 100;
}

/** Nudge with the keyboard, because a 0.01in adjustment is not a mouse gesture. */
    const onKeyDown = (event: React.KeyboardEvent) => {
        if (disabled) return;
        const step = event.shiftKey ? 0.25 : 0.05;
        const by: Record<string, [number, number]> = {
            ArrowLeft: [-step, 0], ArrowRight: [step, 0],
            ArrowUp: [0, step], ArrowDown: [0, -step],
        };
        const delta = by[event.key];
        if (!delta) return;
        event.preventDefault();
        onChange(clampQrPlacement(size, {
            ...placement, left: placement.left + delta[0], bottom: placement.bottom + delta[1],
        }));
    };

    return (
        <div
            ref={boxRef}
            role="group"
            aria-label={`QR code, ${placement.width.toFixed(2)} inches, `
                + `${placement.left.toFixed(2)} from the left and `
                + `${placement.bottom.toFixed(2)} from the bottom of the card`}
            tabIndex={disabled ? -1 : 0}
            onKeyDown={onKeyDown}
            onPointerDown={onPointerDown("move")}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            style={{
                position: "absolute",
                left: `${(origin.left / canvas.width) * 100}%`,
                bottom: `${(origin.bottom / canvas.height) * 100}%`,
                width: `${(origin.size / canvas.width) * 100}%`,
                height: `${(origin.size / canvas.height) * 100}%`,
                cursor: disabled ? "default" : "grab",
                touchAction: "none",
                outline: "none",
                // Promote to its own layer so a drag repaints the square, not the artwork.
                willChange: "left, bottom, width, height",
            }}
        >
            {/* The code itself is drawn by the caller as children, so this component owns
                placement and nothing else. */}
            <div style={{ width: "100%", height: "100%", position: "relative" }}>
                {children}
                <div style={{
                    position: "absolute", inset: 0,
                    outline: disabled ? "none" : "1px dashed rgba(79,70,229,0.9)",
                    outlineOffset: 2,
                    pointerEvents: "none",
                }} />
                {!disabled && (
                    <span
                        onPointerDown={onPointerDown("resize")}
                        onPointerMove={onPointerMove}
                        onPointerUp={endDrag}
                        onPointerCancel={endDrag}
                        role="slider"
                        aria-label="QR code size"
                        aria-valuemin={0.9}
                        aria-valuemax={2}
                        aria-valuenow={Number(placement.width.toFixed(2))}
                        tabIndex={0}
                        style={{
                            position: "absolute", right: -7, top: -7,
                            width: 14, height: 14, borderRadius: 3,
                            background: "#4f46e5", border: "2px solid #fff",
                            boxShadow: "0 1px 3px rgba(0,0,0,.3)",
                            cursor: "nesw-resize", touchAction: "none",
                        }}
                    />
                )}
            </div>
            <span style={{
                position: "absolute", top: "calc(100% + 4px)", left: 0,
                fontSize: 10, color: "#4f46e5", whiteSpace: "nowrap", pointerEvents: "none",
            }}>
                {placement.width.toFixed(2)}in
            </span>

            {/* Below the code and only while dragging, so it never covers the artwork it is
                explaining. `role="status"` rather than `alert`: it is guidance during a
                gesture, not an error somebody has to dismiss. */}
            {limit && (
                <span
                    role="status"
                    style={{
                        position: "absolute", top: "calc(100% + 18px)", left: 0,
                        width: 210, fontSize: 10, lineHeight: 1.35,
                        color: "#b45309", background: "#fffbeb",
                        border: "1px solid #fcd34d", borderRadius: 4, padding: "3px 5px",
                        pointerEvents: "none", zIndex: 2,
                    }}
                >
                    {limit}
                </span>
            )}
            {/* Announced rather than drawn: the card's own edges already show the bounds, and
                a second outline here would compete with the safe-area guide.

                Hidden with inline styles, not a utility class. This component is rendered
                into the editor, the review card and a screenshot harness, and there is no
                `visually-hidden` class in this codebase -- the text simply printed on the
                card, over the artwork. */}
            <span style={{
                position: "absolute", width: 1, height: 1, margin: -1, padding: 0,
                overflow: "hidden", clip: "rect(0 0 0 0)", clipPath: "inset(50%)",
                whiteSpace: "nowrap", border: 0,
            }}>
                {`Card is ${card.width} by ${card.height} inches; keep ${QR_EDGE_MARGIN}in clear of the edges.`}
            </span>
        </div>
    );
}
